// Chunked jacket-file upload. Splits the file into base64 chunks, POSTs each to
// Jacket/UploadChunk, then calls the finalize endpoint (UploadPaper for a new
// paper jacket, UploadAttachment for an existing jacket's document).

import { postJson } from './http';
import type { JacketAttachment } from '@/types/api';

const CHUNK_SIZE = 1024 * 1024; // 1 MB (base64 inflates ~33% → ~1.3 MB/request)

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Read failed'));
    reader.readAsDataURL(blob);
  });
}

/** Prefer the server's error message (e.g. an unsupported-type rejection). */
function msg(res: { message?: unknown }, fallback: string): string {
  return typeof res?.message === 'string' && res.message ? res.message : fallback;
}

async function uploadChunks(file: File, onProgress?: (fraction: number) => void): Promise<void> {
  const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * CHUNK_SIZE, Math.min(file.size, (i + 1) * CHUNK_SIZE));
    const chunk = await blobToDataUrl(blob);
    const res = await postJson<{ err: string | number; next?: number; message?: string }>(
      '/api/V1/Jacket/UploadChunk',
      { name: file.name, index: i, chunk },
    );
    if (res.err !== '1' && res.err !== 1) throw new Error(msg(res, 'Chunk upload failed.'));
    onProgress?.((i + 1) / total);
  }
}

export const uploadRealApi = {
  /**
   * Upload a scanned PDF as a new paper jacket → returns the new jacket id.
   * When `read` is true, the backend reads the PDF with Claude synchronously and
   * fills the jacket; `ocrApplied` reports whether fields were written.
   */
  async uploadPaperJacket(
    file: File,
    onProgress?: (f: number) => void,
    read = true,
  ): Promise<{ jacketId: number; ocrApplied: boolean }> {
    await uploadChunks(file, onProgress);
    const res = await postJson<{ err: string | number; jacketId?: number; ocrApplied?: boolean; message?: string }>(
      '/api/V1/Jacket/UploadPaper',
      { name: file.name, read },
    );
    if ((res.err !== '1' && res.err !== 1) || !res.jacketId) throw new Error(msg(res, 'Could not finalize the upload.'));
    return { jacketId: res.jacketId, ocrApplied: !!res.ocrApplied };
  },

  /** Remove an attachment: drops it from the jacket, deletes local, soft-deletes S3. */
  async removeJacketDoc(jacketId: number, docId: number): Promise<void> {
    const res = await postJson<{ err: string | number }>(
      '/api/V1/Jacket/RemoveDoc',
      { jacketId, docId },
    );
    if (res.err !== '1' && res.err !== 1) throw new Error('Could not remove the attachment.');
  },

  /** List the documents attached to a jacket. */
  async getJacketAttachments(jacketId: number): Promise<JacketAttachment[]> {
    const res = await postJson<{ err: string | number; data?: JacketAttachment[] }>(
      '/api/V1/Jacket/Attachments',
      { jacketId },
    );
    if (res.err !== '1' && res.err !== 1) throw new Error('Could not load attachments.');
    return res.data ?? [];
  },

  /** Attach a document to an existing jacket → returns the new doc id. */
  async uploadJacketAttachment(jacketId: number, file: File, onProgress?: (f: number) => void): Promise<{ docId: number }> {
    await uploadChunks(file, onProgress);
    const res = await postJson<{ err: string | number; docId?: number; message?: string }>(
      '/api/V1/Jacket/UploadAttachment',
      { jacketId, name: file.name },
    );
    if ((res.err !== '1' && res.err !== 1) || !res.docId) throw new Error(msg(res, 'Could not finalize the upload.'));
    return { docId: res.docId };
  },
};
